import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';
import '../common.scss';

// ----------------------------------------------------------------------
const emptyMonthRow = () => ({
  month: '',
  purchases: '',
  purchase_tax: '',
  sales: '',
  sales_tax: '',
});

const emptyGstinBlock = () => ({
  gstin_no: '',
  state_name: '',
  months: [emptyMonthRow()],
});
// ----------------------------------------------------------------------

const toNum = (v) => parseFloat(v) || 0;

const GstSheet = (props) => {
  const { leadId } = props;

  const [gstinBlocks, setGstinBlocks] = useState([emptyGstinBlock()]);
  const [preloaderStatus, setPreloaderStatus] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // ---- Fetch existing GST sheet data for this lead ----
  const getGstSheet = async () => {
    if (!leadId) return;
    try {
      setLoadingList(true);

      const obj = { lead_id: leadId };

      const response = await fetch(`${config.API_BASE_URL}getGstSheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(obj),
      });

      const result = await response.json();

      if (result?.success === 1) {
        const data = result?.data || [];
        if (Array.isArray(data) && data.length > 0) {
          setGstinBlocks(data);
        } else {
          setGstinBlocks([emptyGstinBlock()]);
        }
      } else {
        setGstinBlocks([emptyGstinBlock()]);
      }
    } catch (error) {
      console.log('GST Sheet fetch error:', error);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    getGstSheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // ---- GSTIN header field change (gstin_no / state_name) ----
  const handleGstinHeaderChange = (gIndex, e) => {
    const { name, value } = e.target;
    setGstinBlocks((prev) => {
      const updated = [...prev];
      updated[gIndex] = { ...updated[gIndex], [name]: value };
      return updated;
    });
  };

  // ---- Month row field change ----
  const handleMonthChange = (gIndex, mIndex, e) => {
    const { name, value } = e.target;
    setGstinBlocks((prev) => {
      const updated = [...prev];
      const months = [...updated[gIndex].months];
      months[mIndex] = { ...months[mIndex], [name]: value };
      updated[gIndex] = { ...updated[gIndex], months };
      return updated;
    });
  };

  // ---- Add a new GSTIN block ----
  const handleAddGstin = () => {
    setGstinBlocks((prev) => [...prev, emptyGstinBlock()]);
  };

  // ---- Remove a GSTIN block ----
  const handleRemoveGstin = (gIndex) => {
    setGstinBlocks((prev) => prev.filter((_, i) => i !== gIndex));
  };

  // ---- Add a new month row inside a GSTIN block ----
  const handleAddMonth = (gIndex) => {
    setGstinBlocks((prev) => {
      const updated = [...prev];
      updated[gIndex] = {
        ...updated[gIndex],
        months: [...updated[gIndex].months, emptyMonthRow()],
      };
      return updated;
    });
  };

  // ---- Remove a month row ----
  const handleRemoveMonth = (gIndex, mIndex) => {
    setGstinBlocks((prev) => {
      const updated = [...prev];
      updated[gIndex] = {
        ...updated[gIndex],
        months: updated[gIndex].months.filter((_, i) => i !== mIndex),
      };
      return updated;
    });
  };

  // ---- Per-GSTIN totals / averages (like Excel Total & Average rows) ----
  const getGstinTotals = (block) => {
    const months = block.months || [];
    const count = months.length || 1;

    const totalPurchases = months.reduce((sum, m) => sum + toNum(m.purchases), 0);
    const totalPurchaseTax = months.reduce((sum, m) => sum + toNum(m.purchase_tax), 0);
    const totalSales = months.reduce((sum, m) => sum + toNum(m.sales), 0);
    const totalSalesTax = months.reduce((sum, m) => sum + toNum(m.sales_tax), 0);

    return {
      totalPurchases,
      totalPurchaseTax,
      totalSales,
      totalSalesTax,
      avgSales: totalSales / count,
      avgSalesTax: totalSalesTax / count,
      annualizedSales: (totalSales / count) * 12,
      annualizedSalesTax: (totalSalesTax / count) * 12,
    };
  };

  // ---- Grand totals across all GSTINs (like "All States" columns) ----
  const grandTotals = gstinBlocks.reduce(
    (acc, block) => {
      const t = getGstinTotals(block);
      acc.totalPurchases += t.totalPurchases;
      acc.totalPurchaseTax += t.totalPurchaseTax;
      acc.totalSales += t.totalSales;
      acc.totalSalesTax += t.totalSalesTax;
      acc.annualizedSales += t.annualizedSales;
      acc.annualizedSalesTax += t.annualizedSalesTax;
      return acc;
    },
    {
      totalPurchases: 0,
      totalPurchaseTax: 0,
      totalSales: 0,
      totalSalesTax: 0,
      annualizedSales: 0,
      annualizedSalesTax: 0,
    }
  );

  const totalEstimatedAnnualGst = grandTotals.annualizedSales + grandTotals.annualizedSalesTax;

  const fmt = (n) =>
    (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });

  // ---- Save everything ----
  const handleSave = async () => {
    const hasEmptyGstin = gstinBlocks.some((b) => (b.gstin_no || '').trim() === '');
    if (hasEmptyGstin) {
      toast.error('GSTIN No is required for all blocks.', { position: 'top-right' });
      return;
    }

    try {
      setPreloaderStatus(true);

      const params = { lead_id: leadId, gstData: gstinBlocks };

      const response = await fetch(`${config.API_BASE_URL}manageGstSheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(params),
      });

      const result = await response.json();

      if (result?.success === 1) {
        toast.success(result.message, { position: 'top-right' });
        getGstSheet();
      } else {
        toast.error(result?.message || 'Something went wrong.', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error in GST Sheet save:', error);
      toast.error('An error occurred. Please try again.', { position: 'top-right' });
    } finally {
      setPreloaderStatus(false);
    }
  };

  return (
    <div className="gst_sheet_section mt-3">
      {loadingList ? (
        <p>Loading GST sheet...</p>
      ) : (
        <div>
          {gstinBlocks.map((block, gIndex) => {
            const totals = getGstinTotals(block);

            return (
              <div
                key={`gstin-${gIndex}`}
                style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}
              >
                {/* Index circle */}
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    minWidth: '30px',
                    borderRadius: '50%',
                    background: '#0d6efd',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '14px',
                    marginRight: '14px',
                    marginTop: '18px',
                    flexShrink: 0,
                  }}
                >
                  {gIndex + 1}
                </div>

                {/* GSTIN block box */}
                <div className="border rounded p-3" style={{ position: 'relative', flex: 1 }}>
                  {gstinBlocks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveGstin(gIndex)}
                      title="Remove this GSTIN"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        border: 'none',
                        background: '#dc3545',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  )}

                  {/* GSTIN header fields */}
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor={`gstin_no_${gIndex}`}>
                          <span>
                            GSTIN No <a style={{ color: 'red' }}>*</a>
                          </span>
                          <input
                            type="text"
                            id={`gstin_no_${gIndex}`}
                            name="gstin_no"
                            className="form-control"
                            placeholder="e.g. 06AAJCB4484B1ZU"
                            value={block.gstin_no}
                            onChange={(e) => handleGstinHeaderChange(gIndex, e)}
                          />
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor={`state_name_${gIndex}`}>
                          <span>State Name</span>
                          <input
                            type="text"
                            id={`state_name_${gIndex}`}
                            name="state_name"
                            className="form-control"
                            placeholder="e.g. Karnataka"
                            value={block.state_name}
                            onChange={(e) => handleGstinHeaderChange(gIndex, e)}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Monthly table */}
                  <div className="table-responsive mt-3">
                    <table className="table table-bordered table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ minWidth: 140 }}>Month</th>
                          <th style={{ minWidth: 130 }}>Purchases</th>
                          <th style={{ minWidth: 110 }}>Purchase Tax</th>
                          <th style={{ minWidth: 130 }}>Sales</th>
                          <th style={{ minWidth: 110 }}>Sales Tax</th>
                          <th style={{ width: 50 }}> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {block.months.map((m, mIndex) => (
                          <tr key={`month-${gIndex}-${mIndex}`}>
                            <td>
                              <input
                                type="month"
                                name="month"
                                className="form-control form-control-sm"
                                value={m.month}
                                onChange={(e) => handleMonthChange(gIndex, mIndex, e)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                name="purchases"
                                className="form-control form-control-sm"
                                value={m.purchases}
                                onChange={(e) => handleMonthChange(gIndex, mIndex, e)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                name="purchase_tax"
                                className="form-control form-control-sm"
                                value={m.purchase_tax}
                                onChange={(e) => handleMonthChange(gIndex, mIndex, e)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                name="sales"
                                className="form-control form-control-sm"
                                value={m.sales}
                                onChange={(e) => handleMonthChange(gIndex, mIndex, e)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                name="sales_tax"
                                className="form-control form-control-sm"
                                value={m.sales_tax}
                                onChange={(e) => handleMonthChange(gIndex, mIndex, e)}
                              />
                            </td>
                            <td className="text-center">
                              {block.months.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveMonth(gIndex, mIndex)}
                                  title="Remove this month"
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: '#dc3545',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    lineHeight: 1,
                                    padding: 0,
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-light fw-bold">
                          <td>Total</td>
                          <td>{fmt(totals.totalPurchases)}</td>
                          <td>{fmt(totals.totalPurchaseTax)}</td>
                          <td>{fmt(totals.totalSales)}</td>
                          <td>{fmt(totals.totalSalesTax)}</td>
                          <td> </td>
                        </tr>
                        <tr className="table-light">
                          <td>Average</td>
                          <td>-</td>
                          <td>-</td>
                          <td>{fmt(totals.avgSales)}</td>
                          <td>{fmt(totals.avgSalesTax)}</td>
                          <td> </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mt-2"
                    onClick={() => handleAddMonth(gIndex)}
                  >
                    <i className="bi bi-plus-circle"> </i> Add Month
                  </button>

                  <div className="row mt-3">
                    <div className="col-md-6">
                      <span className="text-muted">Annualized Sales (Avg × 12): </span>
                      <strong>{fmt(totals.annualizedSales)}</strong>
                    </div>
                    <div className="col-md-6">
                      <span className="text-muted">Annualized Sales Tax (Avg × 12): </span>
                      <strong>{fmt(totals.annualizedSalesTax)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="mb-4">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddGstin}>
              <i className="bi bi-plus-circle"> </i> Add GSTIN
            </button>
          </div>

          {/* All States / Grand Total summary */}
          <div className="border rounded p-3 mb-4" style={{ background: '#f8f9fa' }}>
            <h6 className="mb-3">All States - Summary</h6>
            <div className="row">
              <div className="col-md-3">
                <div className="text-muted">Total Purchases</div>
                <div className="fw-bold">{fmt(grandTotals.totalPurchases)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Total Purchase Tax</div>
                <div className="fw-bold">{fmt(grandTotals.totalPurchaseTax)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Total Sales</div>
                <div className="fw-bold">{fmt(grandTotals.totalSales)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Total Sales Tax</div>
                <div className="fw-bold">{fmt(grandTotals.totalSalesTax)}</div>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-md-6">
                <span className="text-muted">Estimated Annual GST (Sales, annualized): </span>
                <strong>{fmt(grandTotals.annualizedSales)}</strong>
              </div>
              <div className="col-md-6">
                <span className="text-muted">Total Estimated GST: </span>
                <strong className="text-primary">{fmt(totalEstimatedAnnualGst)}</strong>
              </div>
            </div>
          </div>

          <div className="row mx-0">
            <div className="col-md-12">
              <div className="form-group mb-0">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={preloaderStatus}>
                  {preloaderStatus ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GstSheet;